/* SSM Zambia design tokens.
 *
 * Light is defined on bare :root so a page always has a complete palette.
 * Dark is layered twice - once for the OS preference and once for an explicit
 * data-theme - so the toggle wins in both directions and "system" still works.
 */

:root {
  /* Brand: deep green reads as land/permit authority; copper is Zambia's
     defining mineral and gives the accent a sense of place. */
  --brand-50:  #eaf5ef;
  --brand-100: #cbe7d7;
  --brand-200: #a3d4ba;
  --brand-300: #6fba95;
  --brand-400: #429b74;
  --brand-500: #1f7d58;
  --brand-600: #146346;
  --brand-700: #0f4d37;
  --brand-800: #0b3a29;
  --brand-900: #07271c;

  --copper-100: #f8e6d8;
  --copper-300: #e0a878;
  --copper-500: #c2703a;
  --copper-600: #a3592b;
  --copper-700: #7f431f;

  /* Neutrals carry a faint green cast so greys never fight the brand. */
  --grey-0:   #ffffff;
  --grey-25:  #fafbfa;
  --grey-50:  #f4f6f5;
  --grey-100: #e8ebe9;
  --grey-200: #d5dad7;
  --grey-300: #b4bcb8;
  --grey-400: #8b9490;
  --grey-500: #6a736f;
  --grey-600: #4e5653;
  --grey-700: #3a403e;
  --grey-800: #262a29;
  --grey-900: #171a19;
  --grey-950: #0e100f;

  --red-100:   #fbe3e1;
  --red-500:   #c8372c;
  --red-600:   #a52a21;
  --amber-100: #fdefd4;
  --amber-500: #c98a12;
  --amber-600: #a06d0a;
  --blue-100:  #dde9f7;
  --blue-500:  #2e6db4;

  /* Semantic surface roles */
  --bg:            var(--grey-25);
  --bg-elevated:   var(--grey-0);
  --bg-sunken:     var(--grey-50);
  --bg-inset:      var(--grey-100);
  --border:        var(--grey-200);
  --border-strong: var(--grey-300);

  --text:          var(--grey-900);
  --text-muted:    var(--grey-500);
  --text-subtle:   var(--grey-400);
  --text-inverse:  var(--grey-0);

  --accent:        var(--brand-500);
  --accent-hover:  var(--brand-600);
  --accent-soft:   var(--brand-50);
  --accent-text:   var(--brand-700);
  --on-accent:     #ffffff;

  --focus-ring: 0 0 0 3px color-mix(in srgb, var(--accent) 32%, transparent);

  --success:     var(--brand-500);
  --success-bg:  var(--brand-50);
  --warning:     var(--amber-600);
  --warning-bg:  var(--amber-100);
  --danger:      var(--red-600);
  --danger-bg:   var(--red-100);
  --info:        var(--blue-500);
  --info-bg:     var(--blue-100);

  --shadow-sm: 0 1px 2px rgba(14, 16, 15, 0.06);
  --shadow-md: 0 2px 4px rgba(14, 16, 15, 0.06), 0 4px 12px rgba(14, 16, 15, 0.05);
  --shadow-lg: 0 8px 28px rgba(14, 16, 15, 0.12);

  --radius-sm: 6px;
  --radius:    10px;
  --radius-lg: 16px;
  --radius-pill: 999px;

  /* 4px base scale */
  --sp-1: 4px;   --sp-2: 8px;   --sp-3: 12px;  --sp-4: 16px;
  --sp-5: 20px;  --sp-6: 24px;  --sp-8: 32px;  --sp-10: 40px;
  --sp-12: 48px; --sp-16: 64px;

  --font: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
          'Helvetica Neue', Arial, sans-serif;
  --font-mono: ui-monospace, 'SF Mono', 'Cascadia Mono', Menlo, monospace;

  --text-xs:   0.75rem;
  --text-sm:   0.8125rem;
  --text-base: 0.9375rem;
  --text-md:   1rem;
  --text-lg:   1.125rem;
  --text-xl:   1.375rem;
  --text-2xl:  1.75rem;
  --text-3xl:  2.25rem;

  --header-h: 60px;
  --bottom-nav-h: 60px;
  --sidebar-w: 248px;
  --content-max: 1240px;
}

/* Mobile/PWA type system — ≤1023px only, the same breakpoint the bottom-nav
 * and FAB switch on. The desktop --font/--text-* tokens above are untouched;
 * this only ever overrides them inside the media query, so nothing here can
 * leak onto a wide viewport. Two deliberate differences from desktop:
 *   1. Font family drops 'Inter' (the loaded webfont) in favour of each
 *      platform's own native system face - San Francisco on iOS, Roboto on
 *      Android - which is what "mobile fonts" means on a PWA: text that
 *      looks native to the device, renders instantly with no webfont
 *      flash/fetch, and matches every other app on the phone.
 *   2. The type scale shifts, not just scales down - --text-base goes UP to
 *      1rem/16px (from 0.9375rem), which is the browser's own zoom
 *      threshold: iOS Safari auto-zooms the page on focusing any input under
 *      16px, and every form control here inherits font size (`button,
 *      input, select, textarea { font: inherit }` below), so this also
 *      fixes that zoom-jump on every text input across the PWA. Headings
 *      (xl/2xl/3xl) move the other way, trimmed down so a page title still
 *      fits a 390px-wide screen in one or two lines instead of wrapping. */
@media (max-width: 1023px) {
  :root {
    --font: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue',
            Arial, sans-serif;

    --text-xs:   0.75rem;
    --text-sm:   0.875rem;
    --text-base: 1rem;
    --text-md:   1.0625rem;
    --text-lg:   1.1875rem;
    --text-xl:   1.3125rem;
    --text-2xl:  1.5rem;
    --text-3xl:  1.875rem;

    /* PWA surfaces: a pure white ground with white cards floating on soft
     * shadows, like a native app - the desktop's grey-25 page/white-card
     * split reads as "website" on a phone. Light mode only: both dark
     * blocks below carry higher specificity (:root:not([data-theme]) /
     * :root[data-theme='dark']) so dark mode is untouched by this. */
    --bg:        #ffffff;
    --bg-sunken: var(--grey-25);
    /* Card surface on the PWA: a shade below the pure-white page so cards
     * read as distinct blocks even on panels where soft shadows wash out -
     * white-on-white with shadow-only separation proved invisible on a
     * real handset. Token (not a literal in ui.css) so the explicit dark
     * toggle can restore a proper dark card surface below. */
    --card: var(--grey-50);
  }
}

@media (max-width: 1023px) {
  :root[data-theme='dark'] { --card: var(--grey-900); }
}

/* Dark via OS preference - skipped when the user explicitly chose light.
 * Desktop-only (≥1024px): the PWA stays on its white app ground even when
 * the phone's OS is in dark mode - "the pwa still has a dark background"
 * was the direct feedback from a dark-mode handset. The in-app moon toggle
 * still works everywhere via the explicit [data-theme='dark'] block below,
 * which this media query deliberately does not gate. */
@media (prefers-color-scheme: dark) and (min-width: 1024px) {
  :root:not([data-theme='light']) {
    --bg:            var(--grey-950);
    --bg-elevated:   var(--grey-900);
    --bg-sunken:     #0a0c0b;
    --bg-inset:      var(--grey-800);
    --border:        #2b302e;
    --border-strong: #3d4442;

    --text:         var(--grey-50);
    --text-muted:   var(--grey-400);
    --text-subtle:  var(--grey-500);
    --text-inverse: var(--grey-950);

    --accent:       var(--brand-300);
    --accent-hover: var(--brand-200);
    --accent-soft:  rgba(111, 186, 149, 0.14);
    --accent-text:  var(--brand-200);
    --on-accent:    var(--grey-950);

    --success:    var(--brand-300);
    --success-bg: rgba(111, 186, 149, 0.15);
    --warning:    #e3b45a;
    --warning-bg: rgba(227, 180, 90, 0.15);
    --danger:     #e8776c;
    --danger-bg:  rgba(232, 119, 108, 0.15);
    --info:       #7aa9e0;
    --info-bg:    rgba(122, 169, 224, 0.15);

    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.4);
    --shadow-md: 0 2px 4px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.32);
    --shadow-lg: 0 8px 28px rgba(0, 0, 0, 0.5);
  }
}

/* Explicit dark - must repeat so the toggle overrides a light OS setting. */
:root[data-theme='dark'] {
  --bg:            var(--grey-950);
  --bg-elevated:   var(--grey-900);
  --bg-sunken:     #0a0c0b;
  --bg-inset:      var(--grey-800);
  --border:        #2b302e;
  --border-strong: #3d4442;

  --text:         var(--grey-50);
  --text-muted:   var(--grey-400);
  --text-subtle:  var(--grey-500);
  --text-inverse: var(--grey-950);

  --accent:       var(--brand-300);
  --accent-hover: var(--brand-200);
  --accent-soft:  rgba(111, 186, 149, 0.14);
  --accent-text:  var(--brand-200);
  --on-accent:    var(--grey-950);

  --success:    var(--brand-300);
  --success-bg: rgba(111, 186, 149, 0.15);
  --warning:    #e3b45a;
  --warning-bg: rgba(227, 180, 90, 0.15);
  --danger:     #e8776c;
  --danger-bg:  rgba(232, 119, 108, 0.15);
  --info:       #7aa9e0;
  --info-bg:    rgba(122, 169, 224, 0.15);

  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.4);
  --shadow-md: 0 2px 4px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.32);
  --shadow-lg: 0 8px 28px rgba(0, 0, 0, 0.5);
}

*, *::before, *::after { box-sizing: border-box; }

html, body, #root { height: 100%; }

body {
  margin: 0;
  font-family: var(--font);
  font-size: var(--text-base);
  line-height: 1.55;
  color: var(--text);
  background: var(--bg);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

h1, h2, h3, h4 { margin: 0; font-weight: 650; line-height: 1.25; letter-spacing: -0.011em; }
h1 { font-size: var(--text-2xl); }
h2 { font-size: var(--text-xl); }
h3 { font-size: var(--text-lg); }
p  { margin: 0; }

a { color: var(--accent-text); text-decoration: none; }
a:hover { text-decoration: underline; }

button, input, select, textarea { font: inherit; color: inherit; }

:focus-visible { outline: none; box-shadow: var(--focus-ring); border-radius: var(--radius-sm); }

::selection { background: var(--accent-soft); color: var(--accent-text); }

/* Respect users who ask for less motion. */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}

/* ── Visible scrollbars ────────────────────────────────────────────────
   Windows and macOS both default to overlay scrollbars that fade out when
   idle, so a long page or a tall table reads as "stuck" - people reported
   being unable to scroll content that scrolls perfectly well. Give the
   document and any scrollable panel a permanently visible thumb. Horizontal
   chip strips (.tab-scroll / .filter-scroll) opt out on purpose: they are
   swiped, and a bar under them would be visual noise. */
html {
  scrollbar-width: thin;
  scrollbar-color: var(--border-strong, rgba(0, 0, 0, .28)) transparent;
}
::-webkit-scrollbar { width: 12px; height: 12px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb {
  background: var(--border-strong, rgba(0, 0, 0, .26));
  border: 3px solid transparent;
  background-clip: content-box;
  border-radius: 999px;
}
::-webkit-scrollbar-thumb:hover { background: var(--text-muted); background-clip: content-box; }
/* The swipeable strips keep their hidden bars. */
.tab-scroll::-webkit-scrollbar,
.filter-scroll::-webkit-scrollbar { height: 0; width: 0; }

/* A wide table scrolls sideways inside its own frame - show that bar too,
   otherwise the extra columns look like they simply do not exist. */
.table-wrap { scrollbar-width: thin; }
